import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {CoreEntityConfirmationDialog} from 'shells/core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import {EditableTextfieldContent} from 'shells/editable-textfield-content/editable-textfield-content';
import {NotePencilRegularIcon} from 'shells/icon/svg/note-pencil-regular-icon';
import {NoteRegularIcon} from 'shells/icon/svg/note-regular-icon';
import {PencilOutlineIcon} from 'shells/icon/svg/pencil-outline-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {BaseRow} from 'shells/row/base-row/base-row';
import {Body} from 'shells/typography/body';

import type {
    GetProductsForLead_lead_products as Products,
    GetProductsForLead_lead_products_edges as LeadProductMap,
    GetMarketForLead_lead_market as Market,
} from 'nutshell-graphql-types';

import {refetchForEvent} from '../../../refetch-helpers';

import {ProductPopover} from '../../product-widget/components/product-popover';

import {useAddProductForLead} from '../graphql/hooks/use-add-product-for-lead';
import {useRemoveProductForLead} from '../graphql/hooks/use-remove-product-for-lead';
import {useUpdateProductForLead} from '../graphql/hooks/use-update-product-for-lead';

import {ProductsRowCreateForm} from './products-row-create-form';

type Props = {
    leadId: string;
    products: Products | null | undefined;
    isAddingProduct: boolean;
    setIsAddingProduct: (isAddingProduct: boolean) => void;
    setError: (error: string) => void;
    market: Market | null | undefined;
};

export function ProductTab(props: Props) {
    const {setError, isAddingProduct, setIsAddingProduct} = props;

    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = React.useState<LeadProductMap | null | undefined>(
        null
    );
    const [productNoteToEdit, setProductNoteToEdit] = React.useState<
        LeadProductMap | null | undefined
    >(null);
    const [removeConfirmationForProductId, setRemoveConfirmationForProductId] = React.useState<
        string | null | undefined
    >(null);

    const popoverRef = React.useRef<HTMLElement | null | undefined>(null);
    const rowRefs = React.useRef<{
        [id: string]: {
            current: HTMLElement | null | undefined;
        };
    }>({});
    const removeButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const {addProductForLead} = useAddProductForLead();
    const {updateProductForLead} = useUpdateProductForLead();
    const {removeProductForLead} = useRemoveProductForLead();

    const handleRemoveProduct = React.useCallback(
        (product: LeadProductMap) => {
            removeProductForLead(product)
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e) || '');
                })
                .then(() => {
                    refetchForEvent('productsUpdated');
                });
        },
        [removeProductForLead, setError]
    );

    const handleUpdateProductNote = React.useCallback(
        (product: LeadProductMap, note: string) => {
            updateProductForLead({
                leadProductMap: product.id,
                note: note,
            })
                .then(() => {
                    setProductNoteToEdit(null);
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e) || '');
                });
        },
        [setError, updateProductForLead]
    );

    const getProductRowRef = React.useCallback((productId: string) => {
        if (!rowRefs.current[productId]) {
            rowRefs.current[productId] = {current: null};
        }

        return rowRefs.current[productId];
    }, []);

    const setProductPopover = React.useCallback(
        (product: LeadProductMap) => {
            const rowRef = rowRefs.current[product.id];
            popoverRef.current = rowRef && rowRef.current;
            setSelectedProduct(product);
            setIsPopoverOpen(true);
        },
        [setSelectedProduct, setIsPopoverOpen]
    );

    const handleSelect = React.useCallback(
        (product: {value: string}) => {
            addProductForLead({
                lead: props.leadId,
                product: product.value,
            })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e) || '');
                })
                .then((response) => {
                    if (response && response.data && response.data.leadProductAdd.product) {
                        setProductPopover(response.data.leadProductAdd.product);
                    }
                    refetchForEvent('productsUpdated');
                })
                .finally(() => {
                    setIsAddingProduct(false);
                });
        },
        [addProductForLead, props.leadId, setError, setIsAddingProduct, setProductPopover]
    );

    return (
        <>
            {isAddingProduct && props.market != null && (
                <ProductsRowCreateForm
                    currentMarket={props.market}
                    onSelect={handleSelect}
                    onCancel={() => setIsAddingProduct(false)}
                />
            )}
            {props.products &&
                props.products.edges.map((product: LeadProductMap) => {
                    const toolbarOptions = [
                        {
                            id: 'product-note',
                            tooltipText: product.relationshipNote ? 'Edit note' : 'Add note',
                            icon: product.relationshipNote
                                ? NotePencilRegularIcon
                                : NoteRegularIcon,
                            onClick: () => {
                                setProductNoteToEdit(product);
                            },
                        },
                        {
                            id: 'product-edit',
                            tooltipText: 'Edit product',
                            icon: PencilOutlineIcon,
                            onClick: () => {
                                setProductPopover(product);
                            },
                        },
                        {
                            id: 'product-remove',
                            tooltipText: 'Remove product',
                            icon: TrashRegularIcon,
                            onClick: () => {
                                setRemoveConfirmationForProductId(product.id);
                            },
                            // @ts-expect-error
                            getRef: (ref) => {
                                removeButtonRef.current = ref;
                            },
                        },
                    ];

                    const isEditingNote = Boolean(
                        productNoteToEdit && productNoteToEdit.id === product.id
                    );

                    return (
                        <React.Fragment key={product.id}>
                            <BaseRow
                                title={product.node.name}
                                toolbarProps={{
                                    options: toolbarOptions,
                                    forceHideToolbar: isEditingNote,
                                    forceShowToolbar: removeConfirmationForProductId === product.id,
                                }}
                                type='products'
                                getRowRef={getProductRowRef(product.id)}
                                onClick={() => {
                                    setProductPopover(product);
                                }}
                            >
                                <>
                                    {product.node.sku && (
                                        <Body size='small' as='div'>
                                            {product.node.sku}
                                        </Body>
                                    )}
                                    <Body size='small' as='div'>
                                        {product.price.formattedWithCents}{' '}
                                        {product.quantity > 1 && ` • Qty ${product.quantity}`}
                                    </Body>
                                    {(product.relationshipNote || isEditingNote) && (
                                        <EditableTextfieldContent
                                            value={product.relationshipNote}
                                            onUpdate={(note) =>
                                                handleUpdateProductNote(product, note || '')
                                            }
                                            isMultiline={true}
                                            isEditing={isEditingNote}
                                            setIsEditing={(isEditing) => {
                                                if (isEditing) {
                                                    setProductNoteToEdit(product);
                                                } else {
                                                    setProductNoteToEdit(null);
                                                }
                                            }}
                                        />
                                    )}
                                </>
                            </BaseRow>
                            {removeConfirmationForProductId === product.id && (
                                <CoreEntityConfirmationDialog
                                    anchor={removeButtonRef.current}
                                    type='product'
                                    parentEntity='lead'
                                    onConfirm={() => {
                                        setRemoveConfirmationForProductId(null);
                                        handleRemoveProduct(product);
                                    }}
                                    onCancel={() => {
                                        setRemoveConfirmationForProductId(null);
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            {isPopoverOpen && selectedProduct && popoverRef.current && (
                <ProductPopover
                    anchor={popoverRef.current}
                    product={selectedProduct}
                    onClose={() => setIsPopoverOpen(false)}
                />
            )}
        </>
    );
}
